import React from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { T } from 'src/shared/helpers';
import AreaIcon from 'src/shared/material_icons/maps/category.svg';
import PlusIcon from 'src/shared/material_icons/content/add.svg';
import usePreview3DStore from 'src/components/widgets/preview3D/store';
import { FormControl } from '../../../../../widgets/formControl';
import { BasicButton } from '../../../../../widgets/buttons';
import { Input } from '../../../../../widgets/input';
import { Radio } from '../../../../../widgets/radio';
import { AdvancedSelect } from '../../../../../widgets/select';
import { Dialog, MessageDialog, useDialogManager } from '../../../../../widgets/dialog';
import { showInfoNotification, showWarningNotification } from '../../../../../widgets/notificationToaster';
import { TextArea } from '../../../../../widgets/textArea';
import { Tab, TabList, TabPanel } from '../../../../../widgets/ztabs';
import useComposerStore from '../../configurator-composer/composer/composerStore';
import { ComposerAreaRowAction, ComposerAreaTreeElement } from '../../configurator-composer/composer/components/composerAreaTreeElement';
import { ComposerTemplateNodeAdapter } from '../../configurator-composer/composer/helpers/composerNodesAdapter';
import { PropertiesPanelAccordion } from '../scene-properties-panel/propertiesPanelAccordion';
import { PropertiesPanelLookProvider } from '../scene-properties-panel/propertiesPanelLook';
import { getComposerDefaultTemplate } from '../../configurator-composer/composer/helpers/composerDefaultTemplate';
import { ComposerAreaNode, ComposerAttributeNode, ComposerAttributeType, isComposerAreaLinkedToAttribute } from './attributes/composerAttributesModels';
import ComposerLinksEditor from './attributes/composerLinksEditor';
import {
    ComposerValidationErrorCode,
    hasComposerValidationError,
    isPrintableComposerMesh
} from '../../configurator-composer/composer/helpers/composerAttributeValidation';
import {
    ComposerAddButton,
    ComposerFormGroup,
    ComposerFormSection,
    ComposerFormSectionTitle,
    ComposerFormStack,
    ComposerKindBadge,
    ComposerPanel,
    ComposerSettingsBody,
    ComposerSubjectContent,
    ComposerSubjectHeader,
    ComposerSubjectMark,
    ComposerSubjectMeta,
    ComposerSubjectName,
    ComposerSubjectNameInput,
    ComposerTabCount,
    ComposerTabs,
    ComposerToolbar,
    ComposerToolbarSpacer,
    ComposerUnsupportedNote
} from './composerWorkspace';

const COMPOSER_AREA_DRAG_TYPE = 'application/x-zakeke-composer-area';

const AreaList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 16px;

    > div {
        border: 1px solid var(--composer-border, #d8e0e8);
        background: var(--composer-hover, #f2f5f8);

        &[aria-selected='true'] {
            border-color: var(--composer-accent, #e8650a);
            background: var(--composer-accent-wash, #fdf0e6);
        }
    }
`;

const MoveAreaDialog: React.FC<{
    area: ComposerAreaNode;
    targets: ComposerAttributeNode[];
    onCancel: () => void;
    onConfirm: (target: ComposerAttributeNode) => void;
}> = ({ area, targets, onCancel, onConfirm }) => {
    const [targetID, setTargetID] = React.useState(targets[0]?.id ?? '');
    const selectedTarget = targets.find((target) => target.id === targetID);
    return (
        <Dialog
            title={T._('Move to another attribute', 'Admin')}
            buttons={[
                { label: T._('Cancel', 'Admin'), outline: true, onClick: onCancel },
                { label: T._('Move', 'Admin'), disabled: !selectedTarget, onClick: () => selectedTarget && onConfirm(selectedTarget) }
            ]}
        >
            <div style={{ minWidth: 360, padding: 24 }}>
                <AdvancedSelect
                    aria-label={T._(`Destination for ${area.name}`, 'Admin')}
                    options={targets.map((target) => ({ value: target.id, label: target.name }))}
                    value={selectedTarget ? { value: selectedTarget.id, label: selectedTarget.name } : null}
                    onChange={(option) => setTargetID(option?.value ?? '')}
                    menuPosition='fixed'
                />
            </div>
        </Dialog>
    );
};

const CustomizableAreasEditor: React.FC<{
    selectedAttribute: ComposerAttributeNode;
    onOpenFullEditor?: (area: ComposerAreaNode, templateNodeID: string) => Promise<boolean | void>;
    detailsHost?: HTMLElement | null;
}> = ({ selectedAttribute, onOpenFullEditor, detailsHost }) => {
    const [isOpeningFullEditor, setIsOpeningFullEditor] = React.useState(false);
    const [draggedAreaId, setDraggedAreaId] = React.useState<string | null>(null);
    const { showDialog, closeDialog } = useDialogManager();
    const [
        attributes,
        areas,
        templates,
        selectedAreas,
        sceneObjectList,
        sceneCameraList,
        attributeFace,
        updateAttribute,
        updateArea,
        cloneArea,
        removeArea,
        selectAttributes,
        selectOptions,
        selectAreas,
        setAttributeFace,
        validateAttributes
    ] = useComposerStore((store) => [
        store.attributes,
        store.areas,
        store.templates,
        store.selectedAreas,
        store.sceneObjectList,
        store.sceneCameraList,
        store.attributeFace,
        store.updateAttribute,
        store.updateArea,
        store.cloneArea,
        store.removeArea,
        store.selectAttributes,
        store.selectOptions,
        store.selectAreas,
        store.setAttributeFace,
        store.validateAttributes
    ]);
    const sceneEditor = usePreview3DStore((store) => store.sceneEditor);
    const model = useComposerStore((store) => store.model);
    const linkedAreas = areas
        .filter((area) => isComposerAreaLinkedToAttribute(area, selectedAttribute))
        .sort((left, right) => left.displayOrder - right.displayOrder);
    const selectedArea = selectedAreas?.find((area) => isComposerAreaLinkedToAttribute(area, selectedAttribute)) ?? null;
    const template = getComposerDefaultTemplate(templates, model?.get('defaultDesignTemplateID'));
    const templateSide = selectedArea && template?.design.get('sides').models.find((side) => side.get('sideID') === selectedArea.sideID);
    const sideRestrictions = selectedArea ? template?.restrictions.get(selectedArea.sideID) : undefined;
    const canAddText = sideRestrictions?.userCanAddTexts ?? templateSide?.get('canAddText') ?? true;
    const canAddImage = sideRestrictions?.userCanAddImages ?? templateSide?.get('canAddImage') ?? true;
    const allowedContent = canAddText && canAddImage ? 'both' : canAddText ? 'text' : canAddImage ? 'image' : 'none';
    const activeTab = attributeFace === 'settings' ? 1 : attributeFace === 'links' ? 2 : 0;

    const selectAreaRow = (area: ComposerAreaNode) => {
        selectAttributes([selectedAttribute]);
        selectOptions(null);
        selectAreas([area]);
        setAttributeFace('areas');
    };

    const onAreaRowAction = (action: ComposerAreaRowAction, area: ComposerAreaNode) => {
        if (action === 'move') {
            const targets = attributes.filter(
                (attribute) =>
                    attribute.type === ComposerAttributeType.Attribute &&
                    attribute.attributeType === MPlaza.AttributeType.CustomizableArea &&
                    attribute.id !== selectedAttribute.id
            );
            if (targets.length === 0) {
                showInfoNotification({ message: T._('Create another customizable-area attribute before moving this area.', 'Admin') });
                return;
            }
            showDialog(
                'composerMoveArea',
                <MoveAreaDialog
                    area={area}
                    targets={targets}
                    onCancel={() => closeDialog('composerMoveArea')}
                    onConfirm={(target) => {
                        const moved = { ...area, attributeID: target.idNumeric, attributeGuid: target.id };
                        updateArea(moved);
                        selectAttributes([target]);
                        selectOptions(null);
                        selectAreas([moved]);
                        setAttributeFace('areas');
                        closeDialog('composerMoveArea');
                    }}
                />
            );
            return;
        }
        if (action === 'duplicate') {
            const cloned = cloneArea(area);
            const selected = {
                ...cloned,
                attributeID: area.attributeID,
                attributeGuid: area.attributeGuid,
                displayOrder: Math.max(-1, ...linkedAreas.map((candidate) => candidate.displayOrder)) + 1
            };
            updateArea(selected);
            selectAreaRow(selected);
            return;
        }
        showDialog(
            'confirmDialog',
            <MessageDialog
                title={T._('Confirm Removal', 'Admin')}
                message={T._(`Are you sure you want to remove “${area.name}”?`, 'Admin')}
                buttons={[
                    { label: T._('Cancel', 'Admin'), outline: true, onClick: () => closeDialog('confirmDialog') },
                    {
                        label: T._('Remove', 'Admin'),
                        destructive: true,
                        onClick: () => {
                            closeDialog('confirmDialog');
                            removeArea(area.id);
                            selectAreas(null);
                        }
                    }
                ]}
            />
        );
    };

    const dropArea = (event: React.DragEvent<HTMLDivElement>, target: ComposerAreaNode) => {
        event.preventDefault();
        event.stopPropagation();
        const draggedID = event.dataTransfer.getData(COMPOSER_AREA_DRAG_TYPE);
        const dragged = linkedAreas.find((area) => area.id === draggedID);
        if (!dragged || dragged.id === target.id) return;
        const ordered = [...linkedAreas];
        const from = ordered.findIndex((area) => area.id === dragged.id);
        const to = ordered.findIndex((area) => area.id === target.id);
        const [moved] = ordered.splice(from, 1);
        ordered.splice(to, 0, moved);
        ordered.forEach((area, displayOrder) => updateArea({ ...area, displayOrder }));
        selectAreas([{ ...moved, displayOrder: to }]);
        setDraggedAreaId(null);
    };

    const updateAllowedContent = (value: 'text' | 'image' | 'both') => {
        if (!template || !selectedArea) return;
        const update = useComposerStore.getState().updateTemplateRestriction;
        update(template, selectedArea.sideID, 'userCanAddTexts', value !== 'image');
        update(template, selectedArea.sideID, 'userCanAddImages', value !== 'text');
    };

    const updateSelectedAttribute = (changes: Partial<ComposerAttributeNode>) => {
        updateAttribute({ ...selectedAttribute, ...changes, isNew: true });
        if (changes.name !== undefined || changes.code !== undefined || changes.links !== undefined || changes.linkOperation !== undefined)
            void validateAttributes();
    };

    const addArea = () => {
        const parsed = ComposerTemplateNodeAdapter.fromAreaNode(null);
        const assignedMeshIDs = new Set(areas.flatMap((area) => area.objects.split(';').filter(Boolean)));
        const firstMesh = sceneObjectList.find((mesh) => sceneEditor?.isRealMesh(mesh) && isPrintableComposerMesh(mesh) && !assignedMeshIDs.has(mesh.id));
        const draft = ComposerTemplateNodeAdapter.toNodeArea(parsed.side, parsed.modelSides);
        const name = T._(`Area ${linkedAreas.length + 1}`, 'Admin');
        Object.assign(draft, {
            name,
            attributeID: selectedAttribute.idNumeric,
            attributeGuid: selectedAttribute.id,
            displayOrder: linkedAreas.length,
            width: 10,
            height: 10,
            ppcm: 102.4,
            camera: selectedAttribute.camera || '',
            objects: firstMesh?.id || ''
        });
        const hydrated = ComposerTemplateNodeAdapter.fromAreaNode(draft);
        const area: ComposerAreaNode = {
            ...ComposerTemplateNodeAdapter.toNodeArea(hydrated.side, hydrated.modelSides),
            ...draft,
            originalSide: hydrated.side,
            originalMeshSides: hydrated.modelSides.toArray()
        };
        updateArea(area);
        selectAreas([useComposerStore.getState().areas.find((candidate) => candidate.id === area.id) ?? area]);
        setAttributeFace('areas');
    };

    const updateSelectedArea = (changes: Partial<ComposerAreaNode>) => {
        if (!selectedArea) return;
        updateArea({ ...selectedArea, ...changes });
        if (changes.objects !== undefined) {
            void validateAttributes();
        }
    };
    // Keep non-printable scene nodes selectable: they can be associated with an area,
    // although they cannot display a printed texture in the Full Editor.
    const meshOptions = sceneObjectList.map((mesh) => {
        const isPrintable = !!sceneEditor?.isRealMesh(mesh) && isPrintableComposerMesh(mesh);
        const reason = !isPrintable ? T._('no printable geometry', 'Admin') : '';
        return {
            value: mesh.id,
            label: `${mesh.name || mesh.id}${reason ? ` (${reason})` : ''}`,
            isDisabled: false
        };
    });
    const selectedMeshOptions = selectedArea
        ? selectedArea.objects
              .split(';')
              .filter(Boolean)
              .map(
                  (id) =>
                      meshOptions.find((option) => option.value === id) ?? {
                          value: id,
                          label: `${id} (${T._('unavailable in this scene', 'Admin')})`,
                          isDisabled: true
                      }
              )
        : [];
    const cameraOptions = sceneCameraList.map((camera) => ({ value: camera.id, label: camera.name || camera.id }));
    const attributeNameHasError = hasComposerValidationError(
        selectedAttribute,
        ComposerValidationErrorCode.ATTRIBUTE_NAME_REQUIRED,
        ComposerValidationErrorCode.ATTRIBUTE_NAME_NOT_UNIQUE
    );
    const attributeCodeHasError = hasComposerValidationError(
        selectedAttribute,
        ComposerValidationErrorCode.ATTRIBUTE_CODE_REQUIRED,
        ComposerValidationErrorCode.ATTRIBUTE_CODE_NOT_UNIQUE,
        ComposerValidationErrorCode.STORE_CODE_INVALID
    );
    const areaObjectsHasError = hasComposerValidationError(
        selectedArea,
        ComposerValidationErrorCode.AREA_OBJECTS_REQUIRED,
        ComposerValidationErrorCode.AREA_OBJECTS_NOT_MESH
    );
    const openFullEditor = async () => {
        if (!selectedArea || !template || !onOpenFullEditor || isOpeningFullEditor) return;
        setIsOpeningFullEditor(true);
        try {
            await onOpenFullEditor(selectedArea, template.id);
        } finally {
            setIsOpeningFullEditor(false);
        }
    };

    return (
        <ComposerPanel aria-label={T._('Customizable areas', 'Admin')}>
            <ComposerSubjectHeader>
                <ComposerSubjectMark $kind='attribute'>
                    <AreaIcon />
                </ComposerSubjectMark>
                <ComposerSubjectContent>
                    <ComposerSubjectNameInput
                        aria-label={T._('Attribute name', 'Admin')}
                        isError={attributeNameHasError}
                        value={selectedAttribute.name}
                        onChange={(event) => updateSelectedAttribute({ name: event.target.value })}
                        onBlur={validateAttributes}
                    />
                    <ComposerSubjectMeta>
                        <ComposerKindBadge $kind='attribute'>{T._('Customizable area', 'Admin')}</ComposerKindBadge>
                        <span>{T._(`${linkedAreas.length} ${linkedAreas.length === 1 ? 'area' : 'areas'}`, 'Admin')}</span>
                    </ComposerSubjectMeta>
                </ComposerSubjectContent>
            </ComposerSubjectHeader>
            <ComposerToolbar>
                <ComposerAddButton onClick={addArea}>
                    <PlusIcon />
                    {T._('Add', 'Admin')}
                </ComposerAddButton>
                <ComposerToolbarSpacer />
            </ComposerToolbar>
            <ComposerTabs selectedIndex={activeTab} onSelect={(index) => setAttributeFace(index === 1 ? 'settings' : index === 2 ? 'links' : 'areas')}>
                <TabList hasOverflow>
                    <Tab>
                        {T._('Areas', 'Admin')} <ComposerTabCount>{linkedAreas.length}</ComposerTabCount>
                    </Tab>
                    <Tab>{T._('Settings', 'Admin')}</Tab>
                    <Tab>
                        {T._('Links', 'Admin')} <ComposerTabCount>{selectedAttribute.links.length}</ComposerTabCount>
                    </Tab>
                </TabList>
                <TabPanel>
                    <ComposerSettingsBody>
                        {linkedAreas.length > 0 && (
                            <AreaList role='tree' aria-label={T._('Areas', 'Admin')}>
                                {linkedAreas.map((area) => (
                                    <ComposerAreaTreeElement
                                        key={area.id}
                                        area={area}
                                        indent={0}
                                        selected={selectedArea?.id === area.id}
                                        onSelect={selectAreaRow}
                                        onDoubleClick={selectAreaRow}
                                        onAction={onAreaRowAction}
                                        onDragStart={(event, dragged) => {
                                            event.dataTransfer.setData(COMPOSER_AREA_DRAG_TYPE, dragged.id);
                                            event.dataTransfer.effectAllowed = 'move';
                                            setDraggedAreaId(dragged.id);
                                            selectAreaRow(dragged);
                                        }}
                                        onDragEnd={() => setDraggedAreaId(null)}
                                        onDragOver={(event) => {
                                            if (!draggedAreaId) return;
                                            event.preventDefault();
                                            event.stopPropagation();
                                            event.dataTransfer.dropEffect = 'move';
                                        }}
                                        onDrop={dropArea}
                                    />
                                ))}
                            </AreaList>
                        )}
                        {detailsHost &&
                            createPortal(
                                <>
                                    <ComposerSubjectHeader>
                                        <ComposerSubjectMark $kind='option'>
                                            <AreaIcon />
                                        </ComposerSubjectMark>
                                        <ComposerSubjectContent>
                                            <ComposerSubjectName>{selectedArea?.name || T._('Area details', 'Admin')}</ComposerSubjectName>
                                            <ComposerSubjectMeta>
                                                <ComposerKindBadge $kind='option'>{T._('Area', 'Admin')}</ComposerKindBadge>
                                            </ComposerSubjectMeta>
                                        </ComposerSubjectContent>
                                    </ComposerSubjectHeader>
                                    <ComposerSettingsBody>
                                        {selectedArea ? (
                                            <ComposerFormStack>
                                                <ComposerFormGroup>
                                                    <ComposerFormSectionTitle>{T._('Placement', 'Admin')}</ComposerFormSectionTitle>
                                                    <ComposerFormSection>
                                                        <FormControl label={T._('Name', 'Admin')}>
                                                            <Input
                                                                value={selectedArea.name}
                                                                onChange={(event) => updateSelectedArea({ name: event.target.value })}
                                                            />
                                                        </FormControl>
                                                        <FormControl label={T._('Objects', 'Admin')}>
                                                            <AdvancedSelect
                                                                isMulti
                                                                isError={areaObjectsHasError}
                                                                options={meshOptions}
                                                                value={selectedMeshOptions}
                                                                onChange={(options) =>
                                                                    updateSelectedArea({
                                                                        objects: options.map((option: { value: string }) => option.value).join(';')
                                                                    })
                                                                }
                                                                menuPosition='fixed'
                                                            />
                                                        </FormControl>
                                                        <FormControl label={T._('Camera', 'Admin')}>
                                                            <AdvancedSelect
                                                                isClearable
                                                                options={cameraOptions}
                                                                value={cameraOptions.find((option) => option.value === selectedArea.camera) ?? null}
                                                                onChange={(option) => updateSelectedArea({ camera: option?.value ?? '' })}
                                                                menuPosition='fixed'
                                                            />
                                                        </FormControl>
                                                    </ComposerFormSection>
                                                </ComposerFormGroup>
                                                <ComposerFormGroup>
                                                    <ComposerFormSectionTitle>{T._('Size', 'Admin')}</ComposerFormSectionTitle>
                                                    <ComposerFormSection>
                                                        <FormControl
                                                            label={T._('Width (cm)', 'Admin')}
                                                            caption={T._(
                                                                'Width and height define the physical print area in centimeters. Its outline in the Full Editor uses these proportions. New areas start at 10 × 10 cm.',
                                                                'Admin'
                                                            )}
                                                        >
                                                            <Input
                                                                type='number'
                                                                min='0.01'
                                                                value={selectedArea.width}
                                                                onChange={(event) => updateSelectedArea({ width: Math.max(0.01, Number(event.target.value)) })}
                                                            />
                                                        </FormControl>
                                                        <FormControl label={T._('Height (cm)', 'Admin')}>
                                                            <Input
                                                                type='number'
                                                                min='0.01'
                                                                value={selectedArea.height}
                                                                onChange={(event) => updateSelectedArea({ height: Math.max(0.01, Number(event.target.value)) })}
                                                            />
                                                        </FormControl>
                                                    </ComposerFormSection>
                                                </ComposerFormGroup>
                                                <ComposerFormGroup>
                                                    <PropertiesPanelLookProvider value='scene'>
                                                        <PropertiesPanelAccordion key={selectedArea.id} header={T._('Advanced', 'Admin')} defaultIsOpen={false}>
                                                            <ComposerFormSection>
                                                                <FormControl label={T._('UV channel', 'Admin')}>
                                                                    <Input
                                                                        type='number'
                                                                        min='0'
                                                                        value={selectedArea.uvMap}
                                                                        onChange={(event) =>
                                                                            updateSelectedArea({ uvMap: Math.max(0, Number(event.target.value)) })
                                                                        }
                                                                    />
                                                                </FormControl>
                                                                <FormControl label={T._('Desktop resolution', 'Admin')}>
                                                                    <Input
                                                                        type='number'
                                                                        min='1'
                                                                        placeholder='1024'
                                                                        value={selectedArea.desktopTextureResolution ?? ''}
                                                                        onChange={(event) =>
                                                                            updateSelectedArea({
                                                                                customTextureResolution: true,
                                                                                desktopTextureResolution: event.target.value
                                                                                    ? Number(event.target.value)
                                                                                    : undefined
                                                                            })
                                                                        }
                                                                    />
                                                                </FormControl>
                                                                <FormControl label={T._('Mobile resolution', 'Admin')}>
                                                                    <Input
                                                                        type='number'
                                                                        min='1'
                                                                        placeholder='512'
                                                                        value={selectedArea.mobileTextureResolution ?? ''}
                                                                        onChange={(event) =>
                                                                            updateSelectedArea({
                                                                                customTextureResolution: true,
                                                                                mobileTextureResolution: event.target.value
                                                                                    ? Number(event.target.value)
                                                                                    : undefined
                                                                            })
                                                                        }
                                                                    />
                                                                </FormControl>
                                                            </ComposerFormSection>
                                                        </PropertiesPanelAccordion>
                                                    </PropertiesPanelLookProvider>
                                                </ComposerFormGroup>
                                                <ComposerFormGroup>
                                                    <ComposerFormSectionTitle>{T._('Content', 'Admin')}</ComposerFormSectionTitle>
                                                    <ComposerFormSection>
                                                        <FormControl
                                                            label={T._('Allowed elements', 'Admin')}
                                                            caption={T._(
                                                                'Defaults to Both for new areas. More restrictions are available in the Full Editor → Template Settings.',
                                                                'Admin'
                                                            )}
                                                        >
                                                            <div role='radiogroup' aria-label={T._('Allowed elements', 'Admin')}>
                                                                <Radio
                                                                    id={`composer-area-${selectedArea.id}-text`}
                                                                    name={`composer-area-${selectedArea.id}-content`}
                                                                    label={T._('Text', 'Admin')}
                                                                    value='text'
                                                                    checked={allowedContent === 'text'}
                                                                    onSelect={updateAllowedContent}
                                                                />
                                                                <Radio
                                                                    id={`composer-area-${selectedArea.id}-images`}
                                                                    name={`composer-area-${selectedArea.id}-content`}
                                                                    label={T._('Images', 'Admin')}
                                                                    value='image'
                                                                    checked={allowedContent === 'image'}
                                                                    onSelect={updateAllowedContent}
                                                                />
                                                                <Radio
                                                                    id={`composer-area-${selectedArea.id}-both`}
                                                                    name={`composer-area-${selectedArea.id}-content`}
                                                                    label={T._('Both', 'Admin')}
                                                                    value='both'
                                                                    checked={allowedContent === 'both'}
                                                                    onSelect={updateAllowedContent}
                                                                />
                                                            </div>
                                                            {allowedContent === 'none' && (
                                                                <small>{T._('No customer content is currently allowed for this area.', 'Admin')}</small>
                                                            )}
                                                        </FormControl>
                                                        <FormControl label={T._('Layout', 'Admin')}>
                                                            <BasicButton
                                                                disabled={!template || !onOpenFullEditor || isOpeningFullEditor}
                                                                onClick={() => void openFullEditor()}
                                                            >
                                                                {isOpeningFullEditor ? T._('Saving…', 'Admin') : T._('Open Full Editor…', 'Admin')}
                                                            </BasicButton>
                                                        </FormControl>
                                                        <ComposerUnsupportedNote>
                                                            {T._(
                                                                'The Full Editor edits this area only. New product and area settings are saved before it opens; template edits stay in the Composer draft until you press Save.',
                                                                'Admin'
                                                            )}
                                                        </ComposerUnsupportedNote>
                                                    </ComposerFormSection>
                                                </ComposerFormGroup>
                                            </ComposerFormStack>
                                        ) : (
                                            <ComposerUnsupportedNote>
                                                {linkedAreas.length
                                                    ? T._('Select an area above to edit its placement, size and texture.', 'Admin')
                                                    : T._('This personalisation has no area yet. Add the first area.', 'Admin')}
                                            </ComposerUnsupportedNote>
                                        )}
                                    </ComposerSettingsBody>
                                </>,
                                detailsHost
                            )}
                    </ComposerSettingsBody>
                </TabPanel>
                <TabPanel>
                    <ComposerSettingsBody>
                        <ComposerFormStack>
                            <ComposerFormGroup>
                                <ComposerFormSectionTitle>{T._('General', 'Admin')}</ComposerFormSectionTitle>
                                <ComposerFormSection>
                                    <FormControl label={T._('Code', 'Admin')}>
                                        <Input
                                            isError={attributeCodeHasError}
                                            value={selectedAttribute.code}
                                            onChange={(event) => updateSelectedAttribute({ code: event.target.value })}
                                        />
                                    </FormControl>
                                    <FormControl label={T._('Description', 'Admin')}>
                                        <TextArea
                                            rows={4}
                                            value={selectedAttribute.description}
                                            onChange={(event) => updateSelectedAttribute({ description: event.target.value })}
                                        />
                                    </FormControl>
                                </ComposerFormSection>
                            </ComposerFormGroup>
                        </ComposerFormStack>
                    </ComposerSettingsBody>
                </TabPanel>
                <TabPanel>
                    <ComposerSettingsBody>
                        <ComposerLinksEditor
                            attributes={attributes}
                            links={selectedAttribute.links}
                            logicalOperator={selectedAttribute.linkOperation}
                            selfAttributeID={selectedAttribute.id}
                            subjectLabel={selectedAttribute.name || T._('This attribute', 'Admin')}
                            validationErrors={selectedAttribute.brokenValues}
                            onChange={(links, linkOperation) => updateSelectedAttribute({ links, linkOperation })}
                        />
                    </ComposerSettingsBody>
                </TabPanel>
            </ComposerTabs>
        </ComposerPanel>
    );
};

export default CustomizableAreasEditor;
